import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Pricing } from 'api/responseTypes';
import { useCartContext } from 'context/CartContext/CartContext';
import { useEventTracking } from 'hooks/useEventTracking';
import { GASeatsPartial, isMultiLevelPricing } from 'types/seats';
import { toast } from 'utils/toast';

interface UseSeatsioHandlersOptions {
  pricing: Pricing[];
  eventId: number;
  onUnauthenticated?: () => void;
  isAuthenticated: () => boolean;
  requireAuthentication?: boolean;
}

function getMandatoryInfo(ga: GASeatsPartial, pricing: Pricing[]) {
  const categoryPricing = pricing.find((p) => p.category === ga.category.key.toString());
  const mandatoryType = categoryPricing?.ticketTypes?.find((t) => t.mandatory);
  const nonMandatoryTypes = categoryPricing?.ticketTypes?.filter((t) => !t.mandatory) ?? [];
  return { categoryPricing, mandatoryType, nonMandatoryTypes };
}

export function useSeatsioHandlers({
  pricing,
  eventId,
  onUnauthenticated,
  isAuthenticated,
  requireAuthentication = true,
}: UseSeatsioHandlersOptions) {
  const { updateTickets } = useCartContext();
  const intl = useIntl();
  const { eventTracking } = useEventTracking();
  const isDeselectingChildrenRef = useRef(false);
  const [isDeselectingChildren, setIsDeselectingChildren] = useState(false);

  // Tracks pending non-mandatory selections per category key that are waiting to see
  // if a mandatory ticket arrives before the debounce fires.
  // Key: categoryKey, Value: { ga, ticketType, label, timeoutId }
  const pendingNonMandatoryRef = useRef<
    Map<
      string,
      {
        ga: GASeatsPartial;
        ticketType: string;
        label: string;
        mandatoryType: string;
        timeoutId: ReturnType<typeof setTimeout>;
      }
    >
  >(new Map());

  const showMandatoryWarning = useCallback(
    (ticketTypeLabel: string, mandatoryLabel: string | undefined) => {
      toast({
        title: intl.formatMessage(
          { id: 'seats.requiresMandatoryTicket' },
          { ticketType: ticketTypeLabel, mandatoryType: mandatoryLabel },
        ),
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'bottom-left',
      });
    },
    [intl],
  );

  const handleSeatSelected = useCallback(
    async (ga: GASeatsPartial) => {
      if (!isAuthenticated() && requireAuthentication) {
        onUnauthenticated?.();
        try {
          await ga.deselect();
        } catch (error) {
          console.warn('Failed to deselect seat:', error);
        }
        return;
      }

      if (isMultiLevelPricing(ga.pricing)) {
        const { categoryPricing, mandatoryType } = getMandatoryInfo(ga, pricing);
        const categoryKey = ga.category.key.toString();
        const selectedIsNonMandatory =
          mandatoryType && ga.selectedTicketType !== mandatoryType.ticketType;
        const mandatoryCountInSection =
          ga.selectionPerTicketType?.[mandatoryType?.ticketType ?? ''] ?? 0;

        if (selectedIsNonMandatory && mandatoryCountInSection === 0) {
          // A non-mandatory ticket was selected without a mandatory one present.
          // Instead of immediately blocking, defer enforcement: if a mandatory ticket
          // arrives (user selected both in the popover) the pending check is cancelled.
          const nonMandatoryLabel =
            categoryPricing?.ticketTypes?.find((t) => t.ticketType === ga.selectedTicketType)
              ?.label ??
            ga.selectedTicketType ??
            '';
          const mandatoryLabel = mandatoryType?.label ?? mandatoryType?.ticketType ?? '';

          // Cancel any existing pending check for this category (shouldn't normally stack)
          const existing = pendingNonMandatoryRef.current.get(categoryKey);
          if (existing) {
            clearTimeout(existing.timeoutId);
          }

          const timeoutId = setTimeout(async () => {
            // Re-read the current mandatory count — if the mandatory ticket arrived
            // in the meantime, the pending entry will have been cleared and this
            // timeout was already cancelled. If we're still here, no mandatory came.
            pendingNonMandatoryRef.current.delete(categoryKey);
            showMandatoryWarning(nonMandatoryLabel, mandatoryLabel);
            setIsDeselectingChildren(true);
            try {
              await ga.deselect(ga.selectedTicketType);
            } catch {
              // seat may not be held yet, ignore
            } finally {
              setIsDeselectingChildren(false);
            }
          }, 500);

          pendingNonMandatoryRef.current.set(categoryKey, {
            ga,
            ticketType: ga.selectedTicketType ?? '',
            label: nonMandatoryLabel,
            mandatoryType: mandatoryLabel,
            timeoutId,
          });

          // Don't call updateTickets yet — wait for the debounce outcome
          return;
        }

        // A mandatory ticket was selected. Cancel any pending non-mandatory enforcement
        // for this category — the non-mandatory ticket is now legitimately paired.
        const pending = pendingNonMandatoryRef.current.get(categoryKey);
        if (pending) {
          clearTimeout(pending.timeoutId);
          pendingNonMandatoryRef.current.delete(categoryKey);
        }
      }

      const price = isMultiLevelPricing(ga.pricing)
        ? (
            ga.pricing.ticketTypes.find((t) => t.ticketType === ga.selectedTicketType) ??
            ga.pricing.ticketTypes[0]
          )?.price ?? 0
        : ga.pricing.price;

      eventTracking.seat.selected({
        eventId,
        sector: ga.category.label,
        gate: ga.entrance,
        price,
      });

      // Defer so Seats.io has settled selectionPerTicketType before we snapshot it.
      // For multi-level pricing, updateMultiLevelTickets reads selectionPerTicketType
      // to build the full cart — deferring ensures non-mandatory counts already held
      // in the section are included when the mandatory ticket is confirmed.
      setTimeout(() => updateTickets(ga, pricing), 0);
    },
    [
      isAuthenticated,
      requireAuthentication,
      onUnauthenticated,
      updateTickets,
      eventTracking,
      eventId,
      pricing,
      showMandatoryWarning,
    ],
  );

  const handleSeatRemoved = useCallback(
    async (ga: GASeatsPartial) => {
      if (!isMultiLevelPricing(ga.pricing)) {
        eventTracking.seat.removed({
          eventId,
          sector: ga.category.label,
          gate: ga.entrance,
          price: ga.pricing.price,
        });
        updateTickets(ga, pricing);
        return;
      }

      const { mandatoryType, nonMandatoryTypes } = getMandatoryInfo(ga, pricing);
      const mandatoryKey = mandatoryType?.ticketType ?? '';
      const remainingMandatory = ga.selectionPerTicketType?.[mandatoryKey] ?? 0;
      const totalNonMandatory = nonMandatoryTypes.reduce(
        (sum, t) => sum + (ga.selectionPerTicketType?.[t.ticketType] ?? 0),
        0,
      );

      if (remainingMandatory === 0 && totalNonMandatory > 0) {
        if (isDeselectingChildrenRef.current) return;
        isDeselectingChildrenRef.current = true;
        setIsDeselectingChildren(true);
        try {
          for (const nonMandatory of nonMandatoryTypes) {
            const count = ga.selectionPerTicketType?.[nonMandatory.ticketType] ?? 0;
            for (let i = 0; i < count; i++) {
              try {
                await ga.deselect(nonMandatory.ticketType);
              } catch {
                break;
              }
            }
          }
        } finally {
          isDeselectingChildrenRef.current = false;
          setIsDeselectingChildren(false);
        }
        const nonMandatoryLabels = nonMandatoryTypes.map((t) => t.label ?? t.ticketType).join(', ');
        showMandatoryWarning(nonMandatoryLabels, mandatoryType?.label ?? mandatoryType?.ticketType);
        return;
      }

      const price =
        (
          ga.pricing.ticketTypes.find((t) => t.ticketType === ga.selectedTicketType) ??
          ga.pricing.ticketTypes[0]
        )?.price ?? 0;

      eventTracking.seat.removed({
        eventId,
        sector: ga.category.label,
        gate: ga.entrance,
        price,
      });

      setTimeout(() => updateTickets(ga, pricing), 0);
    },
    [updateTickets, eventTracking, eventId, pricing, showMandatoryWarning],
  );

  return { handleSeatSelected, handleSeatRemoved, isDeselectingChildren };
}
